import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Play, Loader2, Trash2, Check, Headphones, Pause } from "lucide-react";
import api, { getErrorMsg } from "../../lib/api";
import { AudioRecorder } from "./AudioRecorder";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const uhr = (iso) => (iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "");

// Live-Durchsage wie in der Messe: aufnehmen (Countdown) -> Server optimiert -> "Jetzt abspielen" auf den gewaehlten Masten.
// Die Aufnahme bleibt dauerhaft im Event (und in der Durchsagen-Datenbank) - der Kunde loescht sie selbst.
export function LiveDurchsage({ orderPk, files, zielText, keineMasten, busy, onPlay, onChange }) {
  const [modus, setModus] = useState("idle");   // idle | record
  const [recBusy, setRecBusy] = useState(false);
  const [liveId, setLiveId] = useState(null);
  const [hoert, setHoert] = useState(false);
  const player = useRef(null);
  const live = liveId ? files.find(f => f.id === liveId) : null;
  const bereit = live && (live.status === "ready" || live.status === "ready_unprocessed");

  // eigene Aufnahme in Verarbeitung -> schneller nachladen als der normale 5-s-Takt
  useEffect(() => {
    if (!live || live.status !== "processing") return;
    const t = setInterval(onChange, 2000);
    return () => clearInterval(t);
  }, [live, onChange]);
  useEffect(() => {
    if (live?.status === "failed") toast.error(`Live-Durchsage: ${live.error || "Optimierung fehlgeschlagen"}`);
  }, [live?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const speichern = async (file, { name, prerollSec }) => {
    setRecBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("source", "record");
      fd.append("enhance", "1");
      fd.append("live", "1");
      fd.append("preroll_sec", String(prerollSec || 0));
      const r = await api.post(`/mbox/orders/${orderPk}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setLiveId(r.data.id);
      setModus("idle");
      toast.success("Aufnahme wird optimiert – gleich startbereit");
      onChange();
    } catch (e) { toast.error(getErrorMsg(e, "Aufnahme konnte nicht gespeichert werden")); }
    finally { setRecBusy(false); }
  };
  const verwerfen = async () => {
    if (!live) { setLiveId(null); return; }
    if (!window.confirm(`„${live.name}“ aus dieser Veranstaltung löschen?

In Ihrer Durchsagen-Datenbank bleibt die Aufnahme erhalten.`)) return;
    try { await api.delete(`/mbox/orders/${orderPk}/audio/${live.id}`); } catch { /* bereits entfernt */ }
    setLiveId(null);
    onChange();
  };
  const vorhoeren = () => {
    const el = player.current;
    if (!el || !live) return;
    if (hoert) { el.pause(); el.currentTime = 0; setHoert(false); return; }
    el.src = `${API_BASE}/api/mbox/orders/${orderPk}/audio/${live.id}/stream?token=${localStorage.getItem("token")}`;
    el.play().then(() => setHoert(true)).catch(() => toast.error("Vorhören nicht möglich"));
  };

  // Aufnahme ist abgelaufen/geloescht -> zurueck zum Knopf
  useEffect(() => { if (liveId && files.length && !live) setLiveId(null); }, [liveId, live, files.length]);

  return (
    <div className="rounded-xl border-2 border-red-200 bg-red-50/40 p-3" data-testid="live-durchsage">
      {modus === "record" ? (
        <div className="space-y-2">
          <AudioRecorder busy={recBusy} onSave={speichern} defaultName={`Live-Durchsage ${uhr(new Date().toISOString())}`} maxSec={60} />
          <button type="button" onClick={() => setModus("idle")} className="text-xs text-gray-500 underline" data-testid="live-durchsage-cancel">Abbrechen</button>
        </div>
      ) : live ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-red-600 shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate flex-1">{live.name}</span>
            {live.duration_sec != null && <span className="text-xs text-gray-400">{Math.round(live.duration_sec)} s</span>}
            <button type="button" onClick={vorhoeren} disabled={!bereit} title="Vorhören (nur auf diesem Gerät)" data-testid="live-durchsage-preview"
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 disabled:opacity-40 ${hoert ? "bg-fuchsia-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
              {hoert ? <Pause className="w-4 h-4" /> : <Headphones className="w-4 h-4" />}
            </button>
            <button type="button" onClick={verwerfen} title="Löschen" data-testid="live-durchsage-discard"
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white border border-gray-200 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
          <audio ref={player} onEnded={() => setHoert(false)} onPause={() => setHoert(false)} className="hidden" />
          <button type="button" disabled={!bereit || busy || keineMasten} onClick={() => onPlay(live, false)} data-testid="live-durchsage-start"
            className="w-full h-14 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-red-600/20 active:scale-[0.98] disabled:opacity-50 transition">
            {bereit ? <><Play className="w-5 h-5 fill-white" /> Jetzt abspielen auf {zielText}</> : <><Loader2 className="w-5 h-5 animate-spin" /> wird optimiert …</>}
          </button>
          <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
            <span>Gespeichert – bleibt in der Liste unten, bis Sie sie löschen.</span>
            <span className="flex items-center gap-3 shrink-0">
              <button type="button" onClick={() => setModus("record")} className="underline hover:text-red-700" data-testid="live-durchsage-again">Neu aufnehmen</button>
              <button type="button" onClick={() => setLiveId(null)} className="underline hover:text-gray-800 flex items-center gap-1" data-testid="live-durchsage-done"><Check className="w-3 h-3" /> Fertig</button>
            </span>
          </div>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => setModus("record")} disabled={keineMasten} data-testid="live-durchsage-btn"
            className="w-full h-14 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-red-600/20 active:scale-[0.98] disabled:opacity-50 transition">
            <Mic className="w-5 h-5" /> Live-Durchsage
          </button>
          <p className="text-[11px] text-gray-500 mt-2">Jetzt einsprechen, wird automatisch optimiert und startet zeitgleich auf {zielText}. Die Aufnahme wird gespeichert und bleibt, bis Sie sie löschen.</p>
        </>
      )}
    </div>
  );
}
